import React from 'react';
import ReactApexChart from 'react-apexcharts';

const processMostViewedData = (data) => {
  if (!data || data.length === 0) {
    return {
      top10: [],
      other: null,
      totalViews: 0,
    };
  }

  const sortedData = [...data].sort(
    (a, b) => (parseInt(b.TotalViewCount, 10) || 0) - (parseInt(a.TotalViewCount, 10) || 0)
  );

  const top10 = sortedData.slice(0, 10);

  let otherViews = 0;
  if (sortedData.length > 10) {
    otherViews = sortedData
      .slice(10)
      .reduce((sum, item) => sum + (parseInt(item.TotalViewCount, 10) || 0), 0);
  }

  const otherCategory =
    otherViews > 0
      ? {
          PageName: `Others (${sortedData.length - 10} more)`,
          TotalViewCount: otherViews.toString(),
          Percentage: '0',
        }
      : null;

  const totalViews = sortedData.reduce(
    (sum, item) => sum + (parseInt(item.TotalViewCount, 10) || 0),
    0
  );

  return { top10, other: otherCategory, totalViews };
};

const formatNumber = (num) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

const truncatePageName = (name, maxLength = 15) => {
  if (!name) return 'Unknown';
  if (name.length <= maxLength) return name;
  return `${name.substring(0, maxLength - 3)}...`;
};

const MostUsedFeature = ({ mostViewedPages = [] }) => {
  const { top10, other, totalViews } = processMostViewedData(mostViewedPages);
  const chartData = other ? [...top10, other] : top10;

  const series = chartData.map((item) =>
    totalViews > 0 ? Math.round(((parseInt(item.TotalViewCount, 10) || 0) / totalViews) * 100) : 0
  );

  const labels = chartData.map((item) => truncatePageName(item.PageName));
  const viewCounts = chartData.map((item) => parseInt(item.TotalViewCount, 10) || 0);

  const colorPalette = [
    '#1B5E20',
    '#2E7D32',
    '#388E3C',
    '#43A047',
    '#4CAF50',
    '#66BB6A',
    '#81C784',
    '#A5D6A7',
    '#C8E6C9',
    '#E8F5E9',
    '#B0BEC5',
  ];

  const doughnutOptions = {
    chart: {
      type: 'donut',
      height: 180,
      width: 180,
      sparkline: {
        enabled: false,
      },
      animations: {
        enabled: true,
        speed: 500,
      },
    },
    colors: chartData.map((_, index) => colorPalette[Math.min(index, colorPalette.length - 1)]),
    labels,
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              showAlways: true,
              label: 'Total Views',
              fontSize: '9px',
              fontWeight: 'normal',
              color: '#666',
              formatter: function () {
                return formatNumber(totalViews);
              },
            },
            value: {
              show: true,
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#1B5E20',
              formatter: function (val) {
                return formatNumber(val);
              },
            },
          },
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      custom: function ({ series: tooltipSeries, seriesIndex, w }) {
        const pageName = chartData[seriesIndex]?.PageName || 'Unknown';
        const views = viewCounts[seriesIndex];
        const percentage = tooltipSeries[seriesIndex];
        const color = w.config.colors[seriesIndex];

        return `
          <div style="
            padding: 8px 10px;
            background: white;
            border: 1px solid #ddd;
            border-radius: 4px;
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            max-width: 200px;
          ">
            <div style="
              display: flex;
              align-items: center;
              margin-bottom: 5px;
            ">
              <div style="
                width: 10px;
                height: 10px;
                border-radius: 50%;
                background: ${color};
                margin-right: 8px;
                flex-shrink: 0;
              "></div>
              <strong style="
                font-size: 12px;
                color: #333;
                word-break: break-word;
              ">${pageName}</strong>
            </div>
            <div style="font-size: 11px; color: #666; line-height: 1.4;">
              <div>Views: <strong style="color: #1B5E20;">${views.toLocaleString()}</strong></div>
              <div>Percentage: <strong style="color: #1B5E20;">${percentage}%</strong></div>
              <div>Rank: <strong style="color: #1B5E20;">#${seriesIndex + 1}</strong></div>
            </div>
          </div>
        `;
      },
    },
    stroke: {
      width: 1,
      colors: ['#fff'],
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 150,
            height: 150,
          },
        },
      },
    ],
  };

  return (
    <div className="col-12 col-md-6 col-xl-3">
      <div className="card h-100">
        <div style={{ padding: '12px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
            }}
          >
            <h6
              style={{
                color: '#1B5E20',
                fontWeight: 'bold',
                fontSize: '14px',
                margin: 0,
              }}
            >
              Top Viewed Pages
            </h6>
            <span
              style={{
                fontSize: '10px',
                color: '#666',
                backgroundColor: '#f0f0f0',
                padding: '2px 6px',
                borderRadius: '10px',
                fontWeight: '500',
              }}
            />
          </div>

          {mostViewedPages.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '200px',
                color: '#999',
                fontSize: '12px',
              }}
            >
              <div style={{ marginBottom: '8px' }}>No data</div>
              <div>No page view data available</div>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <div
                style={{
                  width: '100%',
                  maxWidth: '180px',
                  height: '180px',
                  position: 'relative',
                  flexShrink: 0,
                }}
              >
                <ReactApexChart
                  options={doughnutOptions}
                  series={series}
                  type="donut"
                  height={180}
                  width={180}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

MostUsedFeature.defaultProps = {
  mostViewedPages: [],
};

export default MostUsedFeature;
